import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Infinity, Search, Timer, Trophy, Star, CircleCheckBig } from 'lucide-react';
import BoardPreview from './BoardPreview.tsx';
import ShortcutCard from './ShortcutCard.tsx';
import LikeButton from './LikeButton.tsx';
import RobotsSpinner from './RobotsSpinner.tsx';
import { useDailyPuzzle } from '../hooks/useDailyPuzzle.ts';
import DailyStreak from './DailyStreak.tsx';
import { useApi } from '../hooks/useApi.ts';
import { useAuth } from '../hooks/useAuth.ts';
import useSettings from '../hooks/useSettings.ts';

function Stat({ label, value, accent, icon }: { label: string; value: string; accent?: boolean; icon?: React.ReactNode }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-display)', fontWeight: 500,
        fontSize: 24, lineHeight: 1, color: accent ? 'var(--accent)' : 'var(--ink)',
      }}>{value}</div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5,
        fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
        letterSpacing: 1.4, marginTop: 4, fontFamily: 'var(--font-mono)',
      }}>{icon}{label}</div>
    </div>
  );
}

function InlineStat({ label, value, icon, accent }: { label: string; value: string; icon: React.ReactNode; accent?: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontFamily: 'var(--font-mono)', fontSize: 15,
    }}>
      <span style={{ display: 'flex', color: accent ? 'var(--best)' : 'var(--ink-soft)' }}>{icon}</span>
      <span style={{ fontWeight: 600, color: accent ? 'var(--best)' : 'var(--ink)', minWidth: 16 }}>{value}</span>
      <span style={{ color: 'var(--ink-soft)' }}>{label}</span>
    </div>
  );
}


export default function HomeScreen() {
  const navigate = useNavigate();
  const { puzzle: dailyPuzzle, stats: dailyStats, generating } = useDailyPuzzle();
  const { hideOtherScores } = useSettings();
  const api = useApi();
  const { isAuthenticated, login } = useAuth();

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    if (dailyStats) {
      setLiked(dailyStats.liked ?? false);
      setLikeCount(dailyStats.likeCount);
    }
  }, [dailyStats]);

  const handleLike = async () => {
    if (!isAuthenticated) { login(); return; }
    if (!dailyStats) return;
    const next = !liked;
    setLiked(next);
    setLikeCount(c => c + (next ? 1 : -1));
    try {
      const res = await api.toggleLike(dailyStats.puzzleId);
      setLiked(res.liked);
      setLikeCount(res.likeCount);
    } catch {
      setLiked(!next);
      setLikeCount(c => c + (next ? -1 : 1));
    }
  };

  const onPlayDaily = () => {
    if (!dailyPuzzle || !dailyStats) return;
    navigate(`/puzzles/solve/${dailyStats.puzzleId}`, {
      state: {
        puzzle: dailyPuzzle,
        apiMeta: {
          puzzleId: dailyStats.puzzleId,
          liked,
          likeCount,
          userBest: dailyStats.userBest ?? null,
          communityBest: dailyStats.overallBest ?? null,
        },
      },
    });
  };

  return (
    <div className="home-screen" style={{ maxWidth: 1080, margin: '0 auto' }}>
      {/* Hero title */}
      <h1 className="anim-stagger-up anim-dur-lg" style={{
        fontFamily: 'var(--font-display)', fontWeight: 500,
        fontSize: 'clamp(40px, 5.4vw, 68px)', lineHeight: 1.04,
        letterSpacing: -0.5, margin: '0 0 6px', color: 'var(--ink)',
      }}>
        WELCOME TO RIKORO
      </h1>
      <p className="anim-stagger-up anim-dur-lg" style={{
        fontSize: 14, color: 'var(--ink-soft)', maxWidth: 560,
        marginTop: 8, marginBottom: 24, lineHeight: 1.45,
        '--anim-delay': '60ms',
      } as React.CSSProperties}>
        <span style={{ color: 'var(--accent)', fontWeight: 500}}>RIKORO</span> is the online version of Ricochet Robots, a puzzle game about sliding robots to their targets in as few moves as possible.
      </p>

      {/* Daily card grid */}
      <div className="puzzle-card card home-daily-card anim-stagger-up anim-dur-lg" onClick={onPlayDaily} style={{
        '--anim-delay': '120ms',
        display: 'grid', gridTemplateColumns: '1.1fr .5fr', gap: 28,
        marginBottom: 24, alignItems: 'stretch',
        borderRadius: 'var(--radius-card)', padding: '28px 32px',
        position: 'relative', cursor: 'pointer',
      } as React.CSSProperties}>
        {dailyPuzzle ? (
          <>
            <div className="home-daily-info">
              <div className="home-daily-header">
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 2,
                  color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 4,
                }}>Today's puzzle</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 500,
                  }}>{dailyPuzzle.name}</div>
                  <LikeButton liked={liked} count={likeCount} onLike={handleLike} stopPropagation />
                </div>
              </div>
            </div>

            <div className="home-board-preview">
              <div className="home-board-clip">
                <BoardPreview puzzle={dailyPuzzle} fluid />
                <div className="home-board-ink" aria-hidden="true" />
                <div className="home-board-stats">
                  <InlineStat label="Solves" icon={<CircleCheckBig size={16} strokeWidth={1.8} />} value={dailyStats?.solveCount != null ? String(dailyStats.solveCount) : '—'} />
                  <InlineStat label="Personal Best" icon={<Trophy size={16} strokeWidth={1.8} />} value={dailyStats?.userBest != null ? String(dailyStats.userBest) : '—'} />
                  <InlineStat label="Community Best" icon={<Star size={16} strokeWidth={1.8} />} value={hideOtherScores ? '?' : (dailyStats?.overallBest != null ? String(dailyStats.overallBest) : '—')} accent />
                </div>
              </div>
            </div>
            {dailyStats?.userBest == null && <div className="home-board-tag">New</div>}
          </>
        ) : (
          // Loading / error state — mirrors the loaded layout's footprint so the
          // card doesn't grow (and shove the page down) when the puzzle arrives.
          <>
            <div className="home-daily-info">
              <div className="home-daily-header">
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 2,
                  color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 4,
                }}>Today's puzzle</div>
                <div className="home-daily-skeleton" style={{ height: 32, width: '60%', borderRadius: 6, marginBottom: 4 }} />
              </div>
              <div className="home-daily-stats" style={{ display: 'flex', gap: 28, marginTop: 4 }}>
                <div className="home-daily-skeleton" style={{ height: 40, width: 64, borderRadius: 6 }} />
                <div className="home-daily-skeleton" style={{ height: 40, width: 64, borderRadius: 6 }} />
              </div>
            </div>

            <div className="home-board-preview" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {generating ? (
                <RobotsSpinner size={60} />
              ) : (
                <span style={{ color: 'var(--muted)', fontFamily: 'var(--font-mono)', fontSize: 13, textAlign: 'center' }}>
                  Could not load today's puzzle. Try refreshing.
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <DailyStreak />

      {/* Or wander */}
      <div>
        <div className="anim-stagger-up" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, '--anim-delay': '200ms' } as React.CSSProperties}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 500, margin: 0, color: 'var(--ink)' }}>More puzzles</h2>
        </div>
        <div className="home-shortcuts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <ShortcutCard icon={<Search size={24} strokeWidth={1.4} />} title="Browse" description="Community puzzles" onClick={() => navigate('/puzzles/browse')} className="anim-stagger-up" style={{ '--anim-delay': '240ms' } as React.CSSProperties} />
          <ShortcutCard icon={<Infinity size={24} strokeWidth={1.4} />} title="Endless" description="Solve forever" onClick={() => navigate('/puzzles/endless')} className="anim-stagger-up" style={{ '--anim-delay': '320ms' } as React.CSSProperties} />
          <ShortcutCard icon={<Timer size={24} strokeWidth={1.4} />} title="Speedrun" description="Race the clock" onClick={() => navigate('/puzzles/speedrun')} className="anim-stagger-up" style={{ '--anim-delay': '280ms' } as React.CSSProperties} />
        </div>
      </div>
    </div>
  );
}
